package com.eladfinish.bible.contest.screens.mikud

import com.eladfinish.bible.contest.currentMilliseconds
import com.eladfinish.bible.contest.screens.mikud.model.MikudLevelInfo
import com.eladfinish.bible.contest.screens.mikud.model.MikudType
import com.russhwolf.settings.Settings

/**
 * Caches the remote-fetched syllabus ([MikudLevelInfo]) per [MikudType] for [CACHE_TTL_MILLIS]
 * (one month), so re-opening the same preset doesn't re-download it every time. A cache miss or
 * an expired entry both read back as a normal "not cached" result -- the caller re-fetches (or
 * falls back to the local asset).
 */
class SyllabusCache(
    private val settings: Settings,
) {

    companion object {
        private const val CACHE_TTL_MILLIS = 30L * 24 * 60 * 60 * 1000 // 1 month
        private const val PART_SEPARATOR = " SYLLABUS_PART "
        private fun contentKey(type: MikudType) = "syllabus_cache_${type.name}"
        private fun timestampKey(type: MikudType) = "syllabus_cache_ts_${type.name}"
    }

    fun get(type: MikudType): MikudLevelInfo? {
        val timestamp = settings.getLong(timestampKey(type), 0L)
        if (timestamp == 0L || currentMilliseconds - timestamp > CACHE_TTL_MILLIS) return null

        val raw = settings.getString(contentKey(type), "")
        val parts = raw.split(PART_SEPARATOR)
        if (parts.size != 3) return null

        return MikudLevelInfo(intro = parts[0], part1 = parts[1], part2 = parts[2])
    }

    fun put(type: MikudType, info: MikudLevelInfo) {
        val raw = listOf(info.intro, info.part1, info.part2).joinToString(PART_SEPARATOR)
        settings.putString(contentKey(type), raw)
        settings.putLong(timestampKey(type), currentMilliseconds)
    }
}
